import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, X, Save } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';
import ImageUpload from './ImageUpload';
import { resolveImg } from '../../api';

const empty = { title: '', date: '', category: 'Actualité', excerpt: '', content: '', image: '', images: [], link: '', linkLabel: '' };

const NewsManager = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get('/news').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing('new'); };
  const openEdit = (item) => { setForm(item); setEditing(item.id); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    try {
      if (editing === 'new') {
        await api.post('/news', form);
        toast.success('Actualité créée');
      } else {
        await api.put(`/news/${editing}`, form);
        toast.success('Actualité modifiée');
      }
      close();
      load();
    } catch (e) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette actualité ?')) return;
    await api.delete(`/news/${id}`);
    toast.success('Actualité supprimée');
    load();
  };

  const field = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div data-testid="news-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">Actualités</h2>
        <button onClick={openNew} data-testid="add-news-btn" className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-fuchsia-500/30 transition-all">
          <Plus size={18} /> Ajouter
        </button>
      </div>

      <div className="grid gap-4">
        {items.map((item) => (
          <div key={item.id} className="min-w-0 flex items-center gap-4 bg-white/5 border border-white/10 rounded-xl p-4">
            {item.image && <img src={resolveImg(item.image)} alt="" className="w-16 h-16 object-cover rounded-lg flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <span className="text-xs text-fuchsia-400">{item.category} • {item.date}</span>
              <h3 className="text-white font-semibold truncate">{item.title}</h3>
              <p className="text-white/50 text-sm truncate">{item.excerpt}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => openEdit(item)} className="p-2 text-white/60 hover:text-fuchsia-400" data-testid={`edit-news-${item.id}`}><Edit2 size={18} /></button>
              <button onClick={() => remove(item.id)} className="p-2 text-white/60 hover:text-red-400" data-testid={`delete-news-${item.id}`}><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6 overflow-y-auto">
          <div className="bg-neutral-900 border border-white/10 rounded-2xl p-8 w-full max-w-2xl my-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">{editing === 'new' ? 'Nouvelle actualité' : 'Modifier'}</h3>
              <button onClick={close} className="text-white/60 hover:text-white"><X size={24} /></button>
            </div>
            <div className="space-y-4">
              <Input label="Titre" value={form.title} onChange={(v) => field('title', v)} />
              <div className="grid grid-cols-2 gap-4">
                <Input label="Date (AAAA-MM-JJ)" value={form.date} onChange={(v) => field('date', v)} placeholder="2026-06-30" />
                <Input label="Catégorie" value={form.category} onChange={(v) => field('category', v)} />
              </div>
              <Input label="Résumé" value={form.excerpt} onChange={(v) => field('excerpt', v)} textarea />
              <Input label="Contenu complet" value={form.content} onChange={(v) => field('content', v)} textarea />
              <ImageUpload value={form.image} onChange={(v) => field('image', v)} label="Image principale" />

              <div className="pt-4 border-t border-white/10">
                <label className="text-white/70 text-sm block mb-2">Lien "En savoir plus" (facultatif)</label>
                <p className="text-white/40 text-xs mb-3">Chemin interne (ex: <code>/inscriptions</code>, <code>/resultats#galas</code>) ou URL externe complète (https://...). Laissez vide pour renvoyer vers l'article dans la page Actualités.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input label="URL / chemin" value={form.link || ''} onChange={(v) => field('link', v)} placeholder="/inscriptions ou https://..." />
                  <Input label="Libellé du bouton (optionnel)" value={form.linkLabel || ''} onChange={(v) => field('linkLabel', v)} placeholder="Lire la suite" />
                </div>
              </div>

              <div className="pt-4 border-t border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-white/70 text-sm">Photos supplémentaires ({(form.images || []).length}/5)</label>
                  {(form.images || []).length < 5 && (
                    <button type="button" onClick={() => setForm({ ...form, images: [...(form.images || []), ''] })} className="text-fuchsia-400 text-sm">+ Ajouter</button>
                  )}
                </div>
                {(form.images || []).map((p, i) => (
                  <div key={i} className="flex items-start gap-2 mb-3">
                    <div className="flex-1"><ImageUpload value={p} onChange={(v) => { const imgs=[...form.images]; imgs[i]=v; setForm({ ...form, images: imgs }); }} label={`Photo ${i + 1}`} /></div>
                    <button type="button" onClick={() => setForm({ ...form, images: form.images.filter((_, idx) => idx !== i) })} className="p-2 mt-6 text-white/60 hover:text-red-400"><X size={16} /></button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={save} data-testid="save-news-btn" className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-lg text-white font-medium"><Save size={18} /> Enregistrer</button>
              <button onClick={close} className="px-6 py-3 bg-white/5 border border-white/10 rounded-lg text-white/70">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const Input = ({ label, value, onChange, textarea, placeholder, type = 'text', testId }) => (
  <div>
    <label className="block text-white/70 text-sm mb-2">{label}</label>
    {textarea ? (
      <textarea data-testid={testId} value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:border-fuchsia-500/50 focus:outline-none" />
    ) : (
      <input data-testid={testId} type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:border-fuchsia-500/50 focus:outline-none" />
    )}
  </div>
);

export default NewsManager;
